import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import Button from '../../Button/Button'
import { showSnackBar } from '../../Snackbar/Snackbar'
import { generatePoaAddressNotice, getDocument, getPoaAddressNotice } from '../../../features/documents/documents'

const NOTICE_TITLE = 'Notice of Change of Address - Attachment to POA'
const GENERATE_FAILED = 'Unable to generate the notice'
const DOWNLOAD_FAILED = 'Unable to download the notice'
const GENERATE_LABEL = 'Generate Address Notice'
const GENERATED_LABEL = 'Notice generated'
const GENERATED_MESSAGE = 'Notice of Change of Address generated'
const ALREADY_GENERATED_MESSAGE = 'Notice of Change of Address already generated'
const CLIENT_ENTRY = 'client'

const PDF_EXTENSION = /\.pdf$/i

const fileNameOf = (notice) => {
  if (!notice.name) return notice.key
  return PDF_EXTENSION.test(notice.name) ? notice.name : `${notice.name}.pdf`
}

const noticeOf = (value) =>
  value && typeof value.key === 'string' && value.key && typeof value.generatedOn === 'string' ? value : null

const entriesOf = (payload) => {
  const client = payload?.eligible === true
    ? [{
      id: CLIENT_ENTRY,
      coApplicantId: null,
      suffix: '',
      successMessage: GENERATED_MESSAGE,
      alreadyMessage: ALREADY_GENERATED_MESSAGE,
      notice: noticeOf(payload.notice),
    }]
    : []
  const coApplicants = Array.isArray(payload?.coApplicants)
    ? payload.coApplicants
      .filter((coApplicant) => coApplicant?.eligible === true && Boolean(coApplicant._id))
      .map((coApplicant) => ({
        id: coApplicant._id,
        coApplicantId: coApplicant._id,
        suffix: `: ${coApplicant.name} (co-applicant)`,
        successMessage: `${GENERATED_MESSAGE} for ${coApplicant.name}`,
        alreadyMessage: `${ALREADY_GENERATED_MESSAGE} for ${coApplicant.name}`,
        notice: noticeOf(coApplicant.notice),
      }))
    : []
  return [...client, ...coApplicants]
}

const GeneratedNotice = ({ notice, label }) => {
  const dispatch = useDispatch()

  const download = async () => {
    const action = await dispatch(getDocument(notice.key))
    const dataUrl = action?.meta?.requestStatus === 'fulfilled' ? action.payload?.data : null
    if (!dataUrl) {
      showSnackBar('error', DOWNLOAD_FAILED)
      return
    }
    const link = document.createElement('a')
    link.href = dataUrl
    link.download = fileNameOf(notice)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <button type="button" className="h-10 text-md text-primary-main underline" onClick={download}>
      {label}
    </button>
  )
}

const GenerateButton = ({ userId, coApplicantId, label, successMessage, alreadyMessage, onGenerated, onDone }) => {
  const dispatch = useDispatch()
  const mountedRef = useRef(true)
  const [working, setWorking] = useState(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const generate = async () => {
    if (working) return
    setWorking(true)
    try {
      const action = await dispatch(generatePoaAddressNotice(coApplicantId ? { userId, coApplicantId } : { userId }))
      if (action?.meta?.requestStatus === 'fulfilled') {
        showSnackBar('success', action.meta.alreadyGenerated ? alreadyMessage : successMessage)
        onGenerated()
        if (mountedRef.current) onDone()
      } else {
        const reason = action?.payload
        showSnackBar('error', typeof reason === 'string' && reason ? reason : GENERATE_FAILED)
      }
    } finally {
      if (mountedRef.current) setWorking(false)
    }
  }

  return (
    <Button type="outlined" classes="border !border-primary-main h-10 text-md" disabled={working} onClick={generate}>
      {working ? 'Generating…' : label}
    </Button>
  )
}

const PoaAddressNoticeButton = ({ userId, onGenerated, refreshKey }) => {
  const dispatch = useDispatch()
  const [answer, setAnswer] = useState({ userId: null, entries: [] })
  const [generated, setGenerated] = useState([])
  const [reload, setReload] = useState(0)

  useEffect(() => {
    let stale = false
    if (!userId) return undefined
    const apply = (entries) => {
      if (stale) return
      setAnswer({ userId, entries })
      setGenerated([])
    }
    Promise.resolve(dispatch(getPoaAddressNotice(userId)))
      .then((action) => apply(action?.meta?.requestStatus === 'fulfilled' ? entriesOf(action.payload) : []))
      .catch(() => apply([]))
    return () => {
      stale = true
    }
  }, [userId, dispatch, reload, refreshKey])

  const entries = userId && answer.userId === userId
    ? answer.entries.filter((entry) => !generated.includes(entry.id))
    : []

  if (entries.length === 0) return null

  return (
    <div className="mr-3" title={NOTICE_TITLE}>
      <div className="flex flex-wrap gap-2">
        {entries.map((entry) => (entry.notice
          ? (
            <GeneratedNotice
              key={`${userId}:${entry.id}`}
              notice={entry.notice}
              label={`${GENERATED_LABEL} ${entry.notice.generatedOn}${entry.suffix}`}
            />
          )
          : (
            <GenerateButton
              key={`${userId}:${entry.id}`}
              userId={userId}
              coApplicantId={entry.coApplicantId}
              label={`${GENERATE_LABEL}${entry.suffix}`}
              successMessage={entry.successMessage}
              alreadyMessage={entry.alreadyMessage}
              onGenerated={onGenerated}
              onDone={() => {
                setGenerated((current) => [...current, entry.id])
                setReload((value) => value + 1)
              }}
            />
          )))}
      </div>
    </div>
  )
}

export default PoaAddressNoticeButton
